import Iconify, { IconifyProps, isIconSpec } from '@components/iconify';
import { Button, SxProps, Theme, ButtonGroup, Menu, MenuItem } from '@mui/material';
import type { Property } from 'csstype';
import { ReactNode, useState } from 'react';
import { useResponsive } from 'src/hooks/use-responsive';
import { buttonLook, type ButtonVariant } from 'src/theme/v2/button-variants';

import Loader from '../atoms/Loader';

/** A single entry of MUI's array `sx` form (the array itself is not a valid entry). */
type SxEntry = Exclude<SxProps<Theme>, readonly unknown[]>;

/**
 * Properties for the JButton component
 * @interface Props
 */
interface Props {
  /** When true, displays a loading spinner instead of an icon */
  loading?: boolean;
  /** Defines the button's visual style based on theme */
  variant?: ButtonVariant;
  /** When true, button takes 100% width of its container */
  fullWidth?: boolean;
  /** Controls the button size (defaults to responsive sizing) */
  size?: 'small' | 'medium' | 'large';
  /** Icon to display at the start of the button */
  startIcon?: IconifyProps | ReactNode;
  /** Icon to display at the end of the button */
  endIcon?: IconifyProps | ReactNode;
  /** Icon color */
  iconColor?: string;
  /** Text displayed on the button */
  label?: string;
  /** Function called when the button is clicked */
  onClick?: (e: React.MouseEvent) => void;
  /** Additional Material-UI styling */
  sx?: SxProps<Theme>;
  /** When true, the button becomes non-interactive */
  disabled?: boolean;
  /** When true, the button is big (much bigger than large) */
  big?: boolean;
  /** When true, applies fully rounded borders (pill shape) */
  rounded?: boolean;
  /** Optional dropdown items to render as a split button on the right side */
  dropdownItems?: Array<{
    label: string;
    onClick: () => void;
    disabled?: boolean;
    icon?: IconifyProps | ReactNode;
  }>;
  /** When true, the dropdown cannot be opened */
  dropDownDisabled?: boolean;
  /** Content alignment */
  justifyContent?: Property.JustifyContent;
}

/**
 * A customizable button component that adapts to different screen sizes and supports various states including loading.
 *
 * Colors, borders, radii and interaction states all come from the MuiButton theme
 * overrides — this component only maps its legacy `variant` token onto MUI's
 * variant+color pair and contributes layout. Anything passed via `sx` is applied
 * last and still wins, as before.
 *
 * @param {Props} props - The component props
 * @returns {React.JSX.Element} The rendered button component
 *
 * @example
 * // Basic usage
 * <JButton label="Click me" onClick={handleClick} />
 *
 * @example
 * // With loading state and icon
 * <JButton
 *   label="Save"
 *   loading={isSaving}
 *   startIcon="save"
 *   onClick={handleSave}
 * />
 */
export default function JButton({
  variant = 'contained',
  iconColor,
  size,
  startIcon,
  endIcon,
  label,
  onClick,
  sx,
  fullWidth,
  loading,
  disabled,
  big,
  rounded,
  dropdownItems,
  dropDownDisabled,
  justifyContent,
}: Props): React.JSX.Element {
  const onMedium = useResponsive('only', 'md');
  const onSmall = useResponsive('down', 'sm');
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const look = buttonLook(variant);
  // `currentColor` resolves against whatever text color the theme applied — including
  // the disabled token, since a loading button is always disabled.
  const spinner = <Loader small color="currentColor" />;

  const computedSize = size || (onMedium ? 'medium' : onSmall ? 'medium' : 'large');
  const isSplit = Array.isArray(dropdownItems) && dropdownItems.length > 0;
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => setMenuAnchor(event.currentTarget);
  const handleCloseMenu = () => setMenuAnchor(null);

  // Layout only — never colors. Caller `sx` stays last so it keeps overriding everything.
  const layoutSx: SxEntry[] = [
    ...(big ? [{ height: 56, fontSize: 18, px: 4, borderRadius: '16px' }] : []),
    ...(rounded ? [{ borderRadius: '999px' }] : []),
    ...(justifyContent ? [{ justifyContent }] : []),
  ];
  const callerSx: SxEntry[] = Array.isArray(sx) ? (sx as SxEntry[]) : sx ? [sx as SxEntry] : [];

  if (isSplit) {
    return (
      <>
        <ButtonGroup
          variant={look.variant}
          color={look.color}
          fullWidth={fullWidth}
          disableElevation
          sx={{ width: fullWidth ? '100%' : 'auto' }}
        >
          <Button
            variant={look.variant}
            color={look.color}
            size={computedSize}
            startIcon={
              startIcon ? (
                loading && !endIcon ? (
                  spinner
                ) : isIconSpec(startIcon) ? (
                  <Iconify icon={startIcon} />
                ) : (
                  startIcon
                )
              ) : (
                loading && !endIcon && spinner
              )
            }
            endIcon={
              endIcon ? (
                loading && !startIcon ? (
                  spinner
                ) : isIconSpec(endIcon) ? (
                  <Iconify icon={endIcon} />
                ) : (
                  endIcon
                )
              ) : null
            }
            onClick={onClick}
            disableElevation
            sx={[{ position: 'relative', zIndex: 2 }, ...(fullWidth ? [{ flex: 1 }] : []), ...layoutSx, ...callerSx]}
            disabled={disabled || loading}
            fullWidth={false}
          >
            {label}
          </Button>
          <Button
            variant={look.variant}
            color={look.color}
            size={computedSize}
            onClick={dropDownDisabled ? undefined : handleOpenMenu}
            disableElevation
            sx={[
              {
                position: 'relative',
                zIndex: 1,
                minWidth: fullWidth ? (computedSize === 'small' ? 28 : 32) : computedSize === 'small' ? 32 : 40,
                maxWidth: fullWidth ? (computedSize === 'small' ? 28 : 32) : 'none',
                px: fullWidth ? 0.5 : 1,
                flexShrink: 0,
              },
            ]}
            disabled={disabled || loading}
          >
            <Iconify icon="mdi:chevron-down" />
          </Button>
        </ButtonGroup>
        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleCloseMenu}>
          {dropdownItems?.map((item, idx) => (
            <MenuItem
              key={`${item.label}-${idx}`}
              onClick={() => {
                handleCloseMenu();
                if (!item.disabled) item.onClick();
              }}
              disabled={item.disabled}
            >
              {item.icon ? isIconSpec(item.icon) ? <Iconify icon={item.icon} /> : item.icon : null}
              {item.icon ? <span style={{ width: 8 }} /> : null}
              {item.label}
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  }

  return (
    <Button
      variant={look.variant}
      color={look.color}
      size={computedSize}
      startIcon={
        startIcon ? (
          loading && !endIcon ? (
            spinner
          ) : isIconSpec(startIcon) ? (
            <Iconify icon={startIcon} color={iconColor} />
          ) : (
            startIcon
          )
        ) : (
          loading && !endIcon && spinner
        )
      }
      endIcon={
        endIcon ? (
          loading && !startIcon ? (
            spinner
          ) : isIconSpec(endIcon) ? (
            <Iconify icon={endIcon} color={iconColor} />
          ) : (
            endIcon
          )
        ) : null
      }
      onClick={onClick}
      sx={[{ WebkitTapHighlightColor: 'transparent' }, ...layoutSx, ...callerSx]}
      fullWidth={fullWidth}
      disabled={disabled || loading}
    >
      {label}
    </Button>
  );
}
